<script setup lang="ts" generic="TRow extends RecordTableRow">
import { computed } from 'vue';
import { parseISO } from 'date-fns';
import { useMediaQuery } from '@vueuse/core';
import { ChevronRight } from 'lucide-vue-next';
import type { ColumnDef, SortingState } from '@tanstack/vue-table';
import { FacetedDataTable, type FacetOption, type FacetSelection } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner.vue';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import { useRecordFormModal } from '@/composables/records/useRecordFormModal';
import { locationLabel } from '@/composables/records/recordsFormat';
import type { RecordTotals } from '@/composables/records/useRecordsAggregation';
import { i18n } from '@/plugins/internationalization/i18n';
import { RECORD_ACTIONS_COLUMN_ID, type RecordTableRow } from '@/types';

/* Typographic placeholders rather than copy: a weight that was never recorded, and the marker tying
   the footer average to its note about which rows it could not include. */
const NO_VALUE = '—';
const INCOMPLETE_MARKER = '*';

/*
 * The chevron column is rendered by this component rather than through the shared cell text: it is a
 * control, not a value.
 */

/*
 * How much of the screen the rows may take before they scroll inside the table instead of pushing
 * the page down. What the cap buys is the totals row: it sits under the column names and holds
 * there while the rows move past it, so the figures stay readable at any scroll position.
 *
 * A class rather than a length: Tailwind only emits arbitrary values it can find in the source.
 */
const MAX_BODY_HEIGHT = 'max-h-[60vh]';

const props = withDefaults(
    defineProps<{
        /**
         * What the page shows and in what order. Every column falls to the shared rendering below,
         * which is what makes this table the same table on every records page.
         */
        columns: ColumnDef<TRow>[];
        rows: TRow[];
        /**
         * The pig/weight figures, which fill the totals row under the column names in the layout
         * every pig-domain records page shares. Omitted by a page whose figures are not shaped like
         * those - see `totalsSubheader`.
         */
        totals?: RecordTotals;
        /**
         * Totals-row text per column id, already formatted, for a page whose figures `totals` above
         * cannot express. A page supplies one or the other; supplying neither leaves the row out.
         */
        totalsSubheader?: Record<string, string>;
        facetOptions: Record<string, FacetOption[]>;
        loading: boolean;
        /** The one thing above the table naming which registrations it is showing. */
        heading: string;
        /** Names the selected week; blank while nothing is selected. */
        subtitle: string;
        sorting: SortingState;
        columnFilters: FacetSelection;
    }>(),
    {
        totals: undefined,
        totalsSubheader: () => ({}),
    }
);

const emit = defineEmits<{
    'update:sorting': [value: SortingState];
    'update:columnFilters': [value: FacetSelection];
    'form-activity': [];
}>();

/**
 * Column ids that render through `cellText`'s location-chain formatting below (`barn`, `to_barn`,
 * `from_barn`). A farm/barn/batch string runs long enough that the table's `auto` layout squeezes it
 * against shorter neighbouring columns without a floor - given once here, in the one place every
 * records page's location cell renders, rather than repeated in each page's own column definitions.
 */
const LOCATION_COLUMN_IDS: ReadonlySet<string> = new Set(['barn', 'to_barn', 'from_barn']);
const LOCATION_COLUMN_MIN_WIDTH = 'min-w-48';

function withLocationColumnWidth(column: ColumnDef<TRow>): ColumnDef<TRow> {
    if (column.id === undefined || !LOCATION_COLUMN_IDS.has(column.id)) {
        return column;
    }

    return {
        ...column,
        meta: {
            ...column.meta,
            headerClass: [column.meta?.headerClass, LOCATION_COLUMN_MIN_WIDTH].filter(Boolean).join(' '),
            cellClass: [column.meta?.cellClass, LOCATION_COLUMN_MIN_WIDTH].filter(Boolean).join(' '),
        },
    };
}

/**
 * Pins the actions column to the right edge of the table's own horizontal scroll area - see
 * `FacetedDataTable`'s `meta.sticky` handling. Every records page declares this column identically
 * (`{ id: RECORD_ACTIONS_COLUMN_ID, header: '', meta: { align: 'right' } }`), so the pin lives here
 * once rather than in each page's column definitions.
 */
function withActionsColumnSticky(column: ColumnDef<TRow>): ColumnDef<TRow> {
    if (column.id !== RECORD_ACTIONS_COLUMN_ID) {
        return column;
    }

    return {
        ...column,
        meta: {
            ...column.meta,
            sticky: 'right',
        },
    };
}

/** `props.columns`, widened with a location-column floor and a pinned actions column - see
 * `withLocationColumnWidth` / `withActionsColumnSticky`. Every read of the page's columns below goes
 * through this rather than the prop directly, so the width and pin hold whichever slot, facet or
 * cell ends up rendering that column. */
const tableColumns = computed(() => props.columns.map(withLocationColumnWidth).map(withActionsColumnSticky));

/**
 * The header facets, read off the columns that declare themselves filterable.
 *
 * Taking the title from the column's own heading is the point: a facet named separately drifts from
 * the column it filters the first time one of the two is reworded.
 */
const facets = computed(() =>
    Object.fromEntries(
        tableColumns.value
            .filter((column) => column.id !== undefined && column.meta?.filterable === true)
            .map((column) => [column.id, { title: String(column.header ?? ''), options: props.facetOptions[column.id as string] ?? [] }])
    )
);

/** Every column rendered as text, paired with the slot name the data table looks for. */
const cellColumns = computed(() =>
    tableColumns.value.filter((column) => column.id !== undefined && column.id !== RECORD_ACTIONS_COLUMN_ID).map((column) => ({ id: column.id as string, slot: `item[${column.id}]` as const }))
);

/**
 * Below this, a records table's Farm / Barn cell no longer has room for both parts - see
 * `locationLabel`'s `compact` option. The records table sits inside the dashboard's fixed sidebar
 * layout, so viewport width tracks the table's own width closely enough that a global media query
 * is simpler than measuring the table's container.
 */
const isNarrow = useMediaQuery('(max-width: 767px)');

/**
 * What one cell reads.
 *
 * The date, the counts, the weights and both location chains render the same way whichever
 * registration type the page shows, so they live here rather than being restated per page.
 */
function cellText(columnId: string, row: TRow): string {
    switch (columnId) {
        case 'date':
            return i18n.global.d(parseISO(row.date), 'short');
        // Named for the level they resolve to - the barn - not for the wire's own `from_location`
        // and `to_location`, which are the registration's own ends and can sit deeper, at a pen or a
        // batch. Those deeper ends are on the wire but not shown.
        case 'from_barn':
            return locationLabel(row.from_farm ?? null, row.from_barn ?? null, { compact: isNarrow.value });
        case 'to_barn':
            return locationLabel(row.to_farm ?? null, row.to_barn ?? null, { compact: isNarrow.value });
        case 'from_section':
            return row.from_section?.name ?? '';
        case 'to_section':
            return row.to_section?.name ?? '';
        case 'barn':
            return locationLabel(row.farm ?? null, row.barn ?? null, { compact: isNarrow.value });
        case 'section':
            return row.section?.name ?? '';
        case 'invoice':
            return row.invoice ?? '';
        case 'customer':
            return row.customer ?? '';
        case 'feed_category':
            return ((row as Record<string, unknown>).feed_mix_category_name as string | null | undefined) ?? NO_VALUE;
        // A deleted/anonymized creator arrives as null.
        case 'created_by':
            return row.created_by ?? NO_VALUE;
        // `decimal` rather than `numeric` for a whole count: it is the app's grouped format, and
        // a four-figure count is unreadable without the separator the weights beside it already get.
        case 'number':
            return i18n.global.n(row.number, 'decimal');
        case 'total_weight':
            return row.total_weight === null ? NO_VALUE : i18n.global.n(row.total_weight, 'decimal');
        case 'avg_weight':
            return row.avg_weight === null ? NO_VALUE : i18n.global.n(row.avg_weight, 'decimal');
        // Slaughters' own weight/discarded/meat-percentage figures - same grouped-decimal treatment
        // as `total_weight` / `avg_weight` above, just under their own column ids since a slaughter
        // carries two independent weight pairs rather than the one every other records page has.
        // The meat percentage is a plain number here, not a `percent`-styled one: the wire already
        // sends it as `61.1`, and its column heading carries the unit.
        case 'total_live_weight':
        case 'avg_live_weight':
        case 'total_slaughter_weight':
        case 'avg_slaughter_weight':
        case 'avg_meat_percent':
        case 'discarded_pigs':
        case 'avg_discarded_weight':
        case 'payment_weight': {
            const value = (row as Record<string, unknown>)[columnId] as number | null;

            return value === null ? NO_VALUE : i18n.global.n(value, 'decimal');
        }
        default:
            // Any column beyond the shared figures and location chains above is a plain string field
            // a page carries on its own row type - Deaths' cause/category are the first of these.
            // The shared table only guarantees `RecordTableRow`'s own fields, so this reads it back
            // untyped rather than widening that type for every page-specific field.
            return String((row as Record<string, unknown>)[columnId] ?? '');
    }
}

function rowIdOf(row: TRow): string {
    return String(row.record_id);
}

/** The slot name a totals-subheader cell for the given column id renders under. */
function totalsSubheaderSlot(columnId: string): `totals[${string}]` {
    return `totals[${columnId}]`;
}

const { stepConfig, initialState, header, loadingRecordId, openRecordForm } = useRecordFormModal();
</script>

<template>
    <div>
        <div class="px-4 pt-4 pb-2 flex items-start justify-between gap-4">
            <div>
                <h2 class="text-lg font-medium">{{ heading }}</h2>
                <p class="text-normal text-gray-500 min-h-6">{{ subtitle }}</p>
            </div>
            <!-- Empty for every page that does not fill it - a page-specific control (Slaughters'
                 column-visibility menu) that belongs beside this table's own heading rather than
                 floating above it, without this component knowing anything about what it is. -->
            <slot name="actions" />
        </div>
        <!-- Only `closing` reloads. A save emits `submission` and then `closing` as the modal goes
             away, and listening to both fired two invalidations for one edit. -->
        <ActionModal :initial-step-index="1" :initial-state="initialState" :step-config="stepConfig" :header="header" @closing="emit('form-activity')">
            <template #default="{ open }">
                <FacetedDataTable
                    :data="rows"
                    :columns="tableColumns"
                    :facets="facets"
                    :loading="loading"
                    :enable-pagination="false"
                    :page="1"
                    :page-size="Math.max(rows.length, 1)"
                    :page-size-options="[]"
                    :max-body-height="MAX_BODY_HEIGHT"
                    :sorting="sorting"
                    :column-filters="columnFilters"
                    :row-id="rowIdOf"
                    @update:sorting="emit('update:sorting', $event)"
                    @update:column-filters="emit('update:columnFilters', $event)"
                >
                    <template v-for="column in cellColumns" :key="column.id" #[column.slot]="{ item }">{{ cellText(column.id, item) }}</template>
                    <template #item[_actions]="{ item }">
                        <div class="flex justify-end">
                            <Button variant="ghost" size="icon" class="rounded-full -mr-1" :disabled="loadingRecordId !== null" @click="openRecordForm(item.record_id, open)">
                                <Spinner v-if="loadingRecordId === item.record_id" class="h-5 w-5" aria-hidden="true" />
                                <ChevronRight v-else class="h-5 w-5" aria-hidden="true" />
                            </Button>
                        </div>
                    </template>

                    <template v-if="totals" #totals[date]>{{ $t('views.pigs.records.table.totals.total') }}</template>
                    <template v-if="totals" #totals[number]>{{ $n(totals!.totalPigs, 'decimal') }}</template>
                    <template v-if="totals" #totals[total_weight]>
                        <span :title="totals!.hasRowsWithoutWeight ? $t('views.pigs.records.table.totals.weightedAverageHint') : undefined">
                            {{ $n(totals!.totalWeight, 'decimal') }}
                            <template v-if="totals!.hasRowsWithoutWeight">{{ INCOMPLETE_MARKER }}</template>
                        </span>
                    </template>
                    <template v-if="totals" #totals[avg_weight]>
                        <span :title="totals!.hasRowsWithoutWeight ? $t('views.pigs.records.table.totals.weightedAverageHint') : undefined">
                            {{ totals!.weightedAverageWeight === null ? NO_VALUE : $n(totals!.weightedAverageWeight, 'decimal') }}
                            <template v-if="totals!.hasRowsWithoutWeight">{{ INCOMPLETE_MARKER }}</template>
                        </span>
                    </template>
                    <template v-for="(text, columnId) in totalsSubheader" :key="columnId" #[totalsSubheaderSlot(columnId)]>{{ text }}</template>
                </FacetedDataTable>
            </template>
        </ActionModal>
    </div>
</template>
